import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Box
} from '@mui/material';
import { Save as SaveIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Visit, visitSchema } from '../types';

const VISIT_TYPES = [
  { value: 'initial', label: 'Initial Visit' },
  { value: 'followup', label: 'Follow-up Visit' },
  { value: 'emergency', label: 'Emergency Visit' },
  { value: 'routine', label: 'Routine Check-in' }
];

const VISIT_OUTCOMES = [
  { value: 'completed', label: 'Completed Successfully' },
  { value: 'rescheduled', label: 'Rescheduled' },
  { value: 'noshow', label: 'No Show' },
  { value: 'cancelled', label: 'Cancelled' }
];

interface VisitDocumentationProps {
  onSave: (visit: Visit) => void;
  selectedVisit: Visit | null;
  onClear: () => void;
}

const initialFormState = {
  visitType: 'initial',
  date: '',
  startTime: '',
  endTime: '',
  studentId: '',
  familyMembers: '',
  observations: '',
  concerns: '',
  recommendations: '',
  outcome: '',
  followUpNeeded: false,
  location: {
    address: '',
    latitude: 0,
    longitude: 0
  }
};

export const VisitDocumentation: React.FC<VisitDocumentationProps> = ({ 
  onSave,
  selectedVisit,
  onClear
}) => {
  const [formData, setFormData] = useState(initialFormState);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedVisit) {
      const [date, time] = selectedVisit.date.split('T');
      setFormData({
        ...selectedVisit,
        date,
        startTime: time,
        concerns: selectedVisit.concerns || ''
      });
    } else {
      setFormData(initialFormState);
    }
  }, [selectedVisit]);

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleChange2 = (field: keyof typeof formData, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      const visitData: Visit = {
        id: selectedVisit?.id || `visit-${Date.now()}`,
        ...formData,
        visitType: formData.visitType as 'initial' | 'followup' | 'emergency' | 'routine',
        outcome: formData.outcome as 'completed' | 'rescheduled' | 'noshow' | 'cancelled',
        date: `${formData.date}T${formData.startTime}`,
        location: {
          address: formData.location.address || 'Not provided',
          latitude: formData.location.latitude || 0,
          longitude: formData.location.longitude || 0
        }
      };

      // Validate the data
      const validatedData = visitSchema.parse(visitData);
      
      // Save the visit
      onSave(validatedData);
      
      // Show success message
      setShowSuccess(true);
      
      // Reset form
      setFormData(initialFormState);
      
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save visit record');
    }
  };

  return (
    <Paper 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack 
          direction="row" 
          justifyContent="space-between" 
          alignItems="center"
        >
          <Typography variant="h6" component="h2">
            {selectedVisit ? 'Edit Visit' : 'New Visit'}
          </Typography>
          {selectedVisit && (
            <Button
              startIcon={<ClearIcon />}
              onClick={onClear}
              color="inherit"
              size="small"
            >
              Clear
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ 
        p: 2,
        flexGrow: 1,
        overflowY: 'auto'
      }}>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>Visit Type</InputLabel>
            <Select
              value={formData.visitType}
              label="Visit Type"
              onChange={(event) => {
                handleChange2('visitType', event.target.value);
              }}
              required
            >
              {VISIT_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Date"
            type="date"
            value={formData.date}
            onChange={handleChange('date')}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Start Time"
              type="time"
              value={formData.startTime}
              onChange={handleChange('startTime')}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="time"
              value={formData.endTime}
              onChange={handleChange('endTime')}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <TextField
            label="Student ID"
            value={formData.studentId}
            onChange={handleChange('studentId')}
            required
            fullWidth
          />

          <TextField
            label="Family Members Present"
            value={formData.familyMembers}
            onChange={handleChange('familyMembers')}
            fullWidth
            multiline
            rows={2}
          />

          <TextField
            label="Observations"
            value={formData.observations}
            onChange={handleChange('observations')}
            required
            fullWidth
            multiline
            rows={3}
          />

          <TextField
            label="Concerns Raised"
            value={formData.concerns}
            onChange={handleChange('concerns')}
            fullWidth
            multiline
            rows={2}
          />

          <TextField
            label="Recommendations"
            value={formData.recommendations}
            onChange={handleChange('recommendations')}
            required
            fullWidth
            multiline
            rows={2}
          />

          <FormControl fullWidth>
            <InputLabel>Visit Outcome</InputLabel>
            <Select
              value={formData.outcome}
              label="Visit Outcome"
              onChange={(event) => handleChange2('outcome', event.target.value)}
              required
            >
              {VISIT_OUTCOMES.map(outcome => (
                <MenuItem key={outcome.value} value={outcome.value}>
                  {outcome.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          fullWidth
        >
          {selectedVisit ? 'Update Visit' : 'Save Visit Record'}
        </Button>
      </Box>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Visit record {selectedVisit ? 'updated' : 'saved'} successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Paper>
  );
};